import { NetworkStatus } from "@apollo/client";
import { Box, Flex, Tag, TagLabel, Text } from "@chakra-ui/react";
import React, {
  forwardRef,
  PropsWithChildren,
  useEffect,
  useMemo,
  useState,
} from "react";
import {
  components,
  GroupBase,
  MenuListProps,
  OptionProps,
  SingleValueProps,
  StylesConfig,
} from "react-select";

import { Alert, LoadingIndicator, Select } from "../../../components";
import useDebounce from "../../../hooks/useDebounce";
import useSelectTheme from "../../../hooks/useSelectTheme";
import {
  CallGuideListItemFragment,
  CallGuideTag,
  useIaCallGuidesV2Query,
} from "../../graphql";

type OptionType = {
  label: string;
  value: CallGuideItem;
};

type GroupedOptions = GroupBase<OptionType>;

type CallGuideItem = CallGuideListItemFragment & {
  tag?: CallGuideTag | null;
};

type OptionLabelProps = OptionType & { hidePosition?: boolean };

type SelectStylesConfig = StylesConfig<
  PropsWithChildren<OptionType>,
  false,
  PropsWithChildren<GroupedOptions>
>;

type SelectStyleFn = (
  provided: Record<string, any>,
  state: Record<string, any>
) => Record<string, any>;

type SelectOverrideEntry = SelectStyleFn | Record<string, any>;

type CallGuideSelectStylesOverride = Partial<
  Omit<SelectStylesConfig, "groupHeading" | "option" | "singleValue">
> & {
  groupHeading?: SelectOverrideEntry;
  option?: SelectOverrideEntry;
  singleValue?: SelectOverrideEntry;
};

const semanticColorVar = (token: string, fallback: string): string =>
  `var(--chakra-colors-${token}, ${fallback})`;

const TAG_STYLES: Record<
  CallGuideTag,
  { bgColor: string; color: string; label: string }
> = {
  [CallGuideTag.Position]: {
    bgColor: "callGuideV2TagPositionBg",
    color: "callGuideV2TagPositionText",
    label: "This Position",
  },
  [CallGuideTag.Assigned]: {
    bgColor: "callGuideV2TagAssignedBg",
    color: "callGuideV2TagAssignedText",
    label: "Assigned",
  },
  [CallGuideTag.Recent]: {
    bgColor: "callGuideV2TagRecentBg",
    color: "callGuideV2TagRecentText",
    label: "Recently Used",
  },
};

const OptionLabel: React.FC<OptionLabelProps> = ({
  label,
  value,
  hidePosition = false,
}) => {
  const tagStyle = value?.tag ? TAG_STYLES[value.tag] : undefined;
  return (
    <>
      <Flex
        alignItems="center"
        justifyContent="space-between"
        fontSize="sm"
        width="100%"
      >
        <Text
          style={{
            WebkitLineClamp: 3,
            overflow: "hidden",
            display: "-webkit-box",
            WebkitBoxOrient: "vertical",
          }}
        >
          {label}
        </Text>
        {tagStyle && (
          <Tag
            bgColor={tagStyle.bgColor}
            fontSize="xs"
            ml={3}
            height="4"
            flexShrink={0}
          >
            <TagLabel color={tagStyle.color}>{tagStyle.label}</TagLabel>
          </Tag>
        )}
      </Flex>
      {!hidePosition && (
        <Box color="callGuideTagPositionText" mt="0.5" fontSize="sm">
          {value.position?.title?.trim() ?? "No Position"}
        </Box>
      )}
    </>
  );
};

const formatGroupLabel: React.FC<GroupedOptions> = ({ label }) => (
  <Box pl="3" fontWeight="bold" color="textSubtle" fontSize="md">
    {label}
  </Box>
);

interface CallGuideSelectProps {
  name?: string;
  placeholder?: string;
  defaultCallGuideId?: string | null;
  onSelect?: (callGuide: CallGuideItem | undefined) => void;
  isDisabled?: boolean;
  positionId?: string;
  autoFocus?: boolean;
  overrideTheme?: CallGuideSelectStylesOverride;
  hidePosition?: boolean;
  autoSelectAssignedGuide?: boolean;
}

export const CallGuideSelectV2 = forwardRef<any, CallGuideSelectProps>(
  (
    {
      name,
      placeholder,
      defaultCallGuideId,
      onSelect,
      isDisabled = false,
      positionId,
      autoFocus = false,
      overrideTheme = {},
      hidePosition = false,
      autoSelectAssignedGuide = false,
    },
    ref
  ) => {
    const optionDefaultBg = semanticColorVar("selectOptionBg", "white");
    const optionFocusBg = semanticColorVar("selectOptionFocusBg", "#EDF8FE");
    const optionTextColor = semanticColorVar("textHeading", "#393F47");
    const selectedValueColor = semanticColorVar("textHeading", "#393F47");
    const {
      groupHeading = {},
      option = {},
      singleValue = {},
      ...restOverrides
    }: CallGuideSelectStylesOverride = overrideTheme ?? {};
    const [theme, styles] = useSelectTheme({
      groupHeading: (
        provided: Record<string, any>,
        state: Record<string, any>
      ) => {
        const base = {
          ...provided,
          textTransform: "none",
        };
        return typeof groupHeading === "function"
          ? groupHeading(base, state)
          : {
              ...base,
              ...groupHeading,
            };
      },
      option: (
        provided: Record<string, any>,
        state: { isFocused?: boolean; isSelected?: boolean }
      ) => {
        const base = {
          ...provided,
          backgroundColor: state?.isFocused ? optionFocusBg : optionDefaultBg,
          color: optionTextColor,
        };

        return typeof option === "function"
          ? option(base, state)
          : {
              ...base,
              ...option,
            };
      },
      singleValue: (
        provided: Record<string, any>,
        state: Record<string, any>
      ) => {
        const base = {
          ...provided,
          color: selectedValueColor,
        };

        return typeof singleValue === "function"
          ? singleValue(base, state)
          : {
              ...base,
              ...singleValue,
            };
      },
      indicatorsContainer: (provided: Record<string, any>) => {
        const result = { ...provided };
        if (isDisabled) result.display = "none";
        return result;
      },
      ...restOverrides,
    } as StylesConfig);

    const [query, setQuery] = useState("");
    const [didUserClearSelect, setDidUserClearSelect] = useState(false);
    const debouncedQuery = useDebounce<string>(query, 500);
    const [callGuide, setCallGuide] = useState<CallGuideItem | null>();

    const [hasMoreData, setHasMoreData] = useState(true);

    const {
      error: callGuidesError,
      data: callGuidesData,
      networkStatus,
      fetchMore,
    } = useIaCallGuidesV2Query({
      variables: {
        query: debouncedQuery,
        positionId: positionId || undefined,
        limit: 50,
        offset: 0,
      },
      notifyOnNetworkStatusChange: true,
      onCompleted: (data) => {
        if (
          !data.currentUser?.callGuidesV2?.length ||
          data.currentUser?.callGuidesV2?.length < 50
        ) {
          setHasMoreData(false);
        }
      },
    });

    const callGuides = callGuidesData?.currentUser?.callGuidesV2;

    const loadMoreGuides = () => {
      if (!hasMoreData || networkStatus !== NetworkStatus.ready) return;
      fetchMore({
        variables: {
          offset: callGuidesData?.currentUser?.callGuidesV2?.length || 0,
        },
        updateQuery: (prev, { fetchMoreResult }) => {
          if (!fetchMoreResult) return prev;
          const newGuides = fetchMoreResult.currentUser?.callGuidesV2;
          if (!newGuides?.length) {
            setHasMoreData(false);
            return prev;
          }
          if (newGuides.length < 50) {
            setHasMoreData(false);
          }
          return {
            ...prev,
            currentUser: {
              // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
              ...prev.currentUser!,
              callGuidesV2: [
                ...(prev.currentUser?.callGuidesV2 ?? []),
                ...newGuides,
              ],
            },
          };
        },
      });
    };

    useEffect(() => {
      if (callGuide) return; // don’t override a manual pick
      const assignedGuides = callGuides?.filter(
        (g) => g.tag === CallGuideTag.Assigned
      );
      if (
        !defaultCallGuideId &&
        assignedGuides?.length === 1 &&
        autoSelectAssignedGuide &&
        !didUserClearSelect &&
        assignedGuides[0].position
      ) {
        setCallGuide(assignedGuides[0]);
        if (onSelect) onSelect(assignedGuides[0]);
        return;
      }
      const defaultGuide = callGuides?.find((g) => g.id === defaultCallGuideId);
      if (defaultGuide) {
        setCallGuide(defaultGuide);
        if (onSelect) onSelect(defaultGuide);
      }
    }, [defaultCallGuideId, callGuidesData, callGuide]);

    const selectOptions = useMemo(() => {
      return callGuides?.map((guide) => ({
        label: guide.name || "",
        value: guide,
      }));
    }, [callGuidesData]);

    const value = callGuide
      ? {
          label: callGuide.name || "",
          value: callGuide,
        }
      : null;

    if (callGuidesError) {
      return <Alert status="error" description="Error loading guides" />;
    }
    return (
      <Box id={`call-guide-select${name ? `-${name}` : ""}`}>
        <Select
          ref={ref}
          name={name}
          theme={theme}
          styles={styles}
          autoFocus={autoFocus}
          isClearable
          menuShouldBlockScroll
          menuShouldScrollIntoView
          menuPlacement="auto"
          placeholder={placeholder}
          components={{
            SingleValue: hidePosition ? CustomValueNoPosition : CustomValue,
            MenuList: CustomMenuList as React.FC<
              MenuListProps<
                PropsWithChildren<OptionType>,
                false,
                PropsWithChildren<GroupedOptions>
              >
            >,
            Option: CustomOption,
          }}
          minMenuHeight={200}
          noOptionsMessage={(value) => ""}
          isLoading={networkStatus === NetworkStatus.loading}
          value={value}
          inputValue={query}
          filterOption={null}
          getOptionValue={(option) => option.value.id}
          options={selectOptions}
          formatGroupLabel={formatGroupLabel}
          onInputChange={(value, action) => {
            if (action.action === "input-change") {
              setQuery(value.toLowerCase());
            }
          }}
          blurInputOnSelect
          onBlur={() => {
            setQuery("");
          }}
          onChange={(option, action) => {
            if (action.action === "clear") {
              setDidUserClearSelect(true);
            }
            const value = (
              option as {
                value: CallGuideItem;
              }
            )?.value;
            setCallGuide(value);
            if (onSelect) onSelect(value);
          }}
          isDisabled={isDisabled}
          data={{
            query,
            count: callGuides?.length,
            loading:
              networkStatus < NetworkStatus.ready &&
              networkStatus !== NetworkStatus.fetchMore,
            loadMoreGuides,
            hasMoreData,
            isLoadingMore: networkStatus === NetworkStatus.fetchMore,
          }}
        />
      </Box>
    );
  }
);

const CustomOption: React.FC<
  OptionProps<
    PropsWithChildren<OptionType>,
    boolean,
    PropsWithChildren<GroupedOptions>
  >
> = (props) => {
  return (
    <components.Option {...props}>
      <Flex alignItems="center" data-testid={props.data.value.id}>
        {props.isSelected && (
          <Box pl="2" flexShrink={0}>
            <img
              width="13px"
              height="11px"
              src="/static/images/blue-checkmark.svg"
            />
          </Box>
        )}
        <Box mx={2} width="100%">
          <OptionLabel {...props.data} />
        </Box>
      </Flex>
    </components.Option>
  );
};

const CustomValue: React.FC<
  SingleValueProps<
    PropsWithChildren<OptionType>,
    boolean,
    PropsWithChildren<GroupedOptions>
  >
> = (props) => {
  return (
    <components.SingleValue {...props}>
      <OptionLabel {...props.getValue()[0]} />
    </components.SingleValue>
  );
};

const CustomValueNoPosition: React.FC<
  SingleValueProps<
    PropsWithChildren<OptionType>,
    boolean,
    PropsWithChildren<GroupedOptions>
  >
> = (props) => {
  return (
    <components.SingleValue {...props}>
      <OptionLabel {...props.getValue()[0]} hidePosition />
    </components.SingleValue>
  );
};

type CallGuidesData = {
  query: string;
  loading: boolean;
  loadMoreGuides: () => void;
  hasMoreData: boolean;
  isLoadingMore: boolean;
  count?: number;
};

const CustomMenuList: React.FC<
  MenuListProps<
    PropsWithChildren<OptionType>,
    false,
    PropsWithChildren<GroupedOptions>
  > & { selectProps: { data: CallGuidesData } }
> = (props) => {
  const { data } = props.selectProps;

  const handleScrollCapture = (event: React.UIEvent<HTMLDivElement>) => {
    const { scrollTop, scrollHeight, clientHeight } = event.currentTarget;
    const scrollPercentage = (scrollTop + clientHeight) / scrollHeight;
    // Trigger load more when user has scrolled 90% of the way down
    if (
      scrollPercentage > 0.9 &&
      data.hasMoreData &&
      !data.isLoadingMore &&
      !data.loading
    ) {
      data.loadMoreGuides();
    }
  };

  if (data.loading) {
    return (
      <Flex p="4" width="100%">
        <LoadingIndicator />
      </Flex>
    );
  }

  return (
    <components.MenuList
      {...props}
      innerProps={{ onScrollCapture: handleScrollCapture }}
    >
      <>
        {data.query.length && data.count !== undefined ? (
          <Box ml="4" color="textMeta" mt="3" mb="2">
            {data.count} results for &apos;{data.query}&apos;
          </Box>
        ) : null}
        {props.options.length ? (
          <>
            {props.children}
            {data.isLoadingMore && (
              <Flex p="4" width="100%" justifyContent="center">
                <LoadingIndicator />
              </Flex>
            )}
            {!data.hasMoreData && props.options.length > 0 && (
              <Box ml="4" color="textMeta" my="3" textAlign="center">
                No more results
              </Box>
            )}
          </>
        ) : data.query.length ? null : (
          <Box ml="4" color="textMeta" my="3">
            No guides found.
          </Box>
        )}
      </>
    </components.MenuList>
  );
};
